/**
 * Section break points — "Part 2 of 6" inside ONE form.
 *
 * A section whose `breakAfter` is true closes a part: the patient finishes it, presses Continue
 * (Save & Continue on a patient-specific link) and moves on to the next part. It is still ONE form:
 * one submission, one PDF, one Open Dental imaging category. Packets are untouched.
 *
 * WHO USES THIS
 *   components/FormRenderer.vue, and only when its component-local `pagingEnabled` prop is set —
 *   which only ViewForm (the patient-fill screen and the builder preview) does. ViewSubmission.vue
 *   and ViewSubmissionForPDF.vue never page: the staff view and the filed PDF stay byte-identical.
 *
 * THE RULES
 *   - A section hidden by its visibility rules is skipped, and so is its break point. The read is
 *     the same one views/renderer/SectionContainer.vue makes: a section is hidden only when
 *     `visibleMap[uniqueId]` is defined and falsy.
 *   - Parts are recomputed live from the visible map: a yes/no answer that reveals five gated
 *     sections, each a break point, turns 1 part into 6.
 *   - A break point on the LAST visible section never produces an empty trailing part.
 *   - There is always at least one part.
 *
 * IMPORT-FREE ON PURPOSE: cypress/node-tests/forms-section-parts.test.js loads this exact file
 * with `new Function()` after stripping the single trailing `export { … }`. Keep it that way —
 * no import, no require, one export block at the very end.
 */

/**
 * The renderer's own visibility read (views/renderer/SectionContainer.vue isSectionVisible).
 * @param {Object} visibleMap
 * @param {string} uniqueId
 * @returns {boolean}
 */
function isHiddenInMap(visibleMap, uniqueId) {
    if (!visibleMap || !uniqueId) return false;
    if (typeof visibleMap[uniqueId] === 'undefined') return false;
    return !visibleMap[uniqueId];
}

/**
 * Split the form's sections into parts.
 *
 * @param {Array<Object>} sortedSections - sections in render order (FORM_BUILDER_METHODS.sortedSections)
 * @param {Object} visibleMap - the renderer's reactive visibility map (mixins/form-renderer/model.js)
 * @returns {Array<{index: number, sectionIds: Set<string>}>} always at least one part
 */
function buildParts(sortedSections, visibleMap) {
    const sections = Array.isArray(sortedSections) ? sortedSections : [];
    const groups = [];
    let current = new Set();

    sections.forEach(section => {
        if (!section) return;
        const id = section.uniqueId;
        // Hidden by a visibility rule: the section is not rendered, so its break point vanishes too.
        if (isHiddenInMap(visibleMap, id)) return;

        current.add(id);
        if (section.breakAfter === true) {
            groups.push(current);
            current = new Set();
        }
    });

    // Sections after the last break point form the final part. A break point on the last visible
    // section leaves `current` empty — no empty trailing part, unless there is no part at all.
    if (current.size > 0 || groups.length === 0) {
        groups.push(current);
    }

    return groups.map((sectionIds, index) => ({ index, sectionIds }));
}

/**
 * Keep a part index inside the parts that exist right now (parts shrink when an answer hides
 * gated sections). NaN / negative / non-numeric → 0.
 *
 * @param {*} i
 * @param {Array} parts
 * @returns {number}
 */
function clampPart(i, parts) {
    const total = Array.isArray(parts) && parts.length > 0 ? parts.length : 1;
    const n = Math.floor(Number(i));
    if (!Number.isFinite(n) || n < 0) return 0;
    return n > total - 1 ? total - 1 : n;
}

/**
 * @param {Array} parts
 * @param {string} uniqueId - a section's uniqueId
 * @returns {number} the part index holding that section, or -1
 */
function partIndexForSection(parts, uniqueId) {
    if (!Array.isArray(parts)) return -1;
    for (let i = 0; i < parts.length; i++) {
        if (parts[i] && parts[i].sectionIds && parts[i].sectionIds.has(uniqueId)) return i;
    }
    return -1;
}

/**
 * controlId → owning section uniqueId. Walks `section.controls[]` and
 * `section.rows[] → formData.rows[rowId].controls[]` — the same walk as
 * mixins/form-renderer/validation.js getHiddenSectionControlIds().
 *
 * @param {Object} formData - { sections, rows, controls }
 * @returns {Object<string, string>}
 */
function buildControlSectionMap(formData) {
    const map = {};
    const sections = formData && formData.sections ? formData.sections : {};
    const rows = formData && formData.rows ? formData.rows : {};

    Object.keys(sections).forEach(sectionId => {
        const section = sections[sectionId];
        if (!section) return;
        const sectionKey = section.uniqueId || sectionId;

        if (Array.isArray(section.controls)) {
            section.controls.forEach(ref => {
                const id = typeof ref === 'string' ? ref : (ref && ref.uniqueId);
                if (id) map[id] = sectionKey;
            });
        }

        if (Array.isArray(section.rows)) {
            section.rows.forEach(rowId => {
                const row = rows[rowId];
                if (!row || !Array.isArray(row.controls)) return;
                row.controls.forEach(ref => {
                    const id = typeof ref === 'string' ? ref : (ref && ref.uniqueId);
                    if (id) map[id] = sectionKey;
                });
            });
        }
    });

    return map;
}

/**
 * The control ids (keys of formData.controls) that live in part `i`.
 *
 * @param {Array} parts
 * @param {Object} formData
 * @param {number} i
 * @returns {Set<string>}
 */
function controlIdsInPart(parts, formData, i) {
    const out = new Set();
    if (!Array.isArray(parts) || !parts[i] || !parts[i].sectionIds) return out;
    const sectionIds = parts[i].sectionIds;
    const map = buildControlSectionMap(formData);
    Object.keys(map).forEach(controlId => {
        if (sectionIds.has(map[controlId])) out.add(controlId);
    });
    return out;
}

/**
 * Which part holds the control behind a validation error key.
 *
 * The key is what libraries/validation.js buckets errors under and what `data-control-name`
 * carries: `control.name || controlId` (controlId is the control's uniqueId). Matching by
 * uniqueId too covers controls whose name is empty.
 *
 * @param {Array} parts
 * @param {Object} formData
 * @param {string} key
 * @returns {number} part index, or -1 when the control is unknown or in no part
 */
function partIndexForControlKey(parts, formData, key) {
    if (!key || !Array.isArray(parts)) return -1;
    const controls = formData && formData.controls ? formData.controls : {};
    const map = buildControlSectionMap(formData);

    let controlId = null;
    const ids = Object.keys(controls);
    for (let i = 0; i < ids.length; i++) {
        const c = controls[ids[i]] || {};
        if ((c.name || ids[i]) === key) { controlId = ids[i]; break; }
    }
    if (controlId === null) {
        for (let i = 0; i < ids.length; i++) {
            const c = controls[ids[i]] || {};
            if (ids[i] === key || c.uniqueId === key) { controlId = ids[i]; break; }
        }
    }
    if (controlId === null || !map[controlId]) return -1;

    return partIndexForSection(parts, map[controlId]);
}

/**
 * FALLBACK SWITCH (see the plan's "Signatures" decision): a copy of the form values with every
 * signature control's value removed, so a saved draft never carries strokes. Not wired by
 * default — drafts carry signatures as-is. Never mutates its input.
 *
 * @param {Object} formInputData
 * @param {Object} formData
 * @returns {Object}
 */
function stripSignatureValues(formInputData, formData) {
    const out = Object.assign({}, formInputData || {});
    const controls = formData && formData.controls ? formData.controls : {};
    Object.keys(controls).forEach(controlId => {
        const c = controls[controlId];
        if (!c || c.type !== 'signature') return;
        delete out[c.name || controlId];
        if (c.uniqueId) delete out[c.uniqueId];
    });
    return out;
}

export {
    buildParts,
    clampPart,
    partIndexForSection,
    buildControlSectionMap,
    controlIdsInPart,
    partIndexForControlKey,
    stripSignatureValues
};
